import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import { designUrl } from "../data/catalog";
import { useWishlist } from "../context/WishlistContext";

/**
 * Detect a "real" desktop pointer (mouse / trackpad with hover). Falls back
 * to false on the server. Recomputed once at module load — good enough for
 * a jewellery browsing session (nobody hot-swaps a mouse mid-scroll).
 */
const IS_HOVER_DEVICE =
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

/**
 * DesignCard
 * - Mobile / touch: video auto-plays whenever the card is on screen (silent
 *   loop), pauses when it scrolls off.
 * - Desktop / hover: card shows the still image by default; video plays only
 *   while the cursor is inside the card.
 * - Heart icon toggles wishlist; anywhere else opens the design detail page
 *   (or calls `onOpen(design)` when provided — used by the related grid).
 *
 * The <video> element is still lazy-mounted via IntersectionObserver so we
 * never inflate DOM for cards nowhere near the viewport, but on desktop
 * mounting no longer implies playing.
 */
export default function DesignCard({ design, onOpen }) {
  const cardRef = useRef(null);
  const videoRef = useRef(null);
  const [videoMounted, setVideoMounted] = useState(false);
  const [inView, setInView] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const navigate = useNavigate();
  const { isSaved, toggle } = useWishlist();
  const saved = isSaved(design.id);

  // Track viewport visibility. On mobile this drives play/pause. On desktop
  // we still use it to lazy-mount + to pause when the card scrolls out even
  // if the cursor happens to still be inside its rectangle.
  useEffect(() => {
    if (!design.video) return undefined;
    const el = cardRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVideoMounted(true);
      setInView(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setVideoMounted(true);
            setInView(true);
          } else {
            setInView(false);
          }
        }
      },
      { rootMargin: "200px 0px", threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [design.video]);

  // Decide whether the video should be playing right now.
  // • Desktop  → only while the cursor is over the card (and it's still on screen)
  // • Mobile   → whenever the card is in view
  const shouldPlay = IS_HOVER_DEVICE ? (hovering && inView) : inView;

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (shouldPlay) {
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    } else {
      try {
        v.pause();
        // Rewind on desktop so the next hover starts from the beginning —
        // feels intentional and "cinematic" rather than picking up mid-clip.
        if (IS_HOVER_DEVICE) v.currentTime = 0;
      } catch (_) {}
    }
  }, [shouldPlay, videoMounted]);

  const openDetail = () => {
    if (onOpen) onOpen(design);
    else navigate(designUrl(design));
  };

  const onHeartClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    toggle(design);
  };

  // Only wire hover handlers when we know we have a hover device — avoids
  // synthetic mouseenter events on mobile from re-triggering pause logic.
  const hoverProps = IS_HOVER_DEVICE
    ? {
        onPointerEnter: (e) => {
          if (e.pointerType === "mouse") setHovering(true);
        },
        onPointerLeave: (e) => {
          if (e.pointerType === "mouse") setHovering(false);
        },
      }
    : {};

  return (
    <div
      className="design-card"
      ref={cardRef}
      role="group"
      aria-label={design.name}
      {...hoverProps}
    >
      <button
        type="button"
        className={`dc-heart ${saved ? "on" : ""}`}
        onClick={onHeartClick}
        aria-label={saved ? "Remove from wishlist" : "Add to wishlist"}
        aria-pressed={saved}
      >
        <Heart
          size={16}
          fill={saved ? "currentColor" : "none"}
          strokeWidth={saved ? 0 : 1.6}
        />
      </button>

      <button
        type="button"
        className="dc-open"
        onClick={openDetail}
        aria-label={`View ${design.name}`}
      >
        <div className="dc-media">
          <img
            src={design.image}
            alt={design.name}
            loading="lazy"
            decoding="async"
          />
          {design.video && videoMounted && (
            <video
              ref={videoRef}
              src={design.video}
              poster={design.image}
              muted
              loop
              playsInline
              webkit-playsinline="true"
              preload="metadata"
              className={`dc-video ${videoReady ? "ready" : ""} ${shouldPlay ? "visible" : ""}`}
              onCanPlay={() => {
                setVideoReady(true);
                if (shouldPlay && videoRef.current && videoRef.current.paused) {
                  const p = videoRef.current.play();
                  if (p && typeof p.catch === "function") p.catch(() => {});
                }
              }}
            />
          )}
        </div>
        <div className="dc-body">
          <span className="dc-num">{design.number}</span>
          <h4>{design.styleName || design.name}</h4>
          {design.limitedCollectionId ? (
            <p className="dc-meta">
              <span className="dc-ltd">Limited</span>
              {design.purity ? ` · ${design.purity}` : ""}
              {design.weight ? ` · ${design.weight}${design.weightUnit || "g"}` : ""}
            </p>
          ) : (
            <p className="dc-meta">
              {design.subCategory.label} · {design.purity} · {design.weight}
              {design.weightUnit}
            </p>
          )}
        </div>
      </button>
    </div>
  );
}
